import React, { FC, memo } from 'react'

import { Trans } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/Button/Button'
import { focusIds } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { LoginSessionType } from '@/constants/login'
import { paths } from '@/constants/paths'
import { usePageRefresher } from '@/hooks/usePageRefresher'
import { getCurrentLanguage } from '@/i18n'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'
import useStore from '@/store/useStore'
import { OfferType } from '@/types/servicesTypes/configTypes'

import {
  SubscriptionNode,
  SubscriptionCard,
  ButtonWrapper,
  DarkeningContainer,
  SubscriptionContent,
  CreditPackageInfo,
  CreditsBadge,
  CreditTitle,
} from './styles'

const SubscriptionItemRaw: FC<{
  i: number
  offer: OfferType
  isBought: boolean
}> = ({ i, offer, isBought }) => {
  const { t } = useCommonTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isPopup = !pathname.includes(paths.EVENT_PAGE)
  const paymentPopup = useStore((state) => state.common.paymentPopup)
  const setPaymentPopup = useStore((state) => state.common.setPaymentPopup)
  const setAuthPopup = useStore((state) => state.common.setAuthPopup)
  const setPpvPopup = usePPVPopupStore((state) => state.setPpvPopup)
  const { pageRefresh } = usePageRefresher()

  const { setCurrentOfferId } = useStore((state) => state.promotion)

  const proceedToCheckout = (offer: OfferType) => {
    if (offer.id === 'CreditsPackage' && paymentPopup) {
      setPpvPopup({
        event: paymentPopup.event,
        eventChannel: paymentPopup.eventChannel,
        prevFocusId: paymentPopup.prevFocusId,
      })
      setPaymentPopup(null)
      return
    }
    const offerId = offer?.offerInfo?.id
    if (!offerId) {
      throw new Error(t('cantCheckout'))
    }

    setCurrentOfferId(offerId)
    if (paymentPopup) {
      setAuthPopup({
        sessionType: LoginSessionType.CHECKOUT,
        prevFocusId: paymentPopup.prevFocusId,
        redirectPath: isPopup
          ? `${paths.EVENT_PAGE}/${paymentPopup.event.id}`
          : '',
        onSuccessHandler: isPopup ? undefined : pageRefresh,
      })
      setPaymentPopup(null)
    } else {
      navigate(`${paths.CHECKOUT}`)
    }
  }

  const buttonText = isBought
    ? t('subscribed')
    : offer.callToActionText || t('subscribe')

  const language = getCurrentLanguage()

  return (
    <SubscriptionNode
      focusId={`${focusIds.SUBSCRIPTION}${i}`}
      onSelected={() => !isBought && proceedToCheckout(offer)}
      $isBought={isBought}
    >
      <SubscriptionCard>
        <SubscriptionContent>
          <img src={offer.image?.sources?.[0]?.url} alt="" />
        </SubscriptionContent>
        {isBought && <DarkeningContainer />}
        {offer.creditPackageInfo && (
          <CreditPackageInfo>
            <CreditsBadge>
              {t('paymentPopup.yourCredits', {
                credits:
                  offer.creditPackageInfo.credits?.toLocaleString(language),
              })}
              <img src={icons.register.swCredits} alt="" />
            </CreditsBadge>
            <CreditTitle>
              <Trans
                defaults={t('paymentPopup.purchaseByCredits', {
                  cost: offer.creditPackageInfo.cost?.toLocaleString(language),
                  count: offer.creditPackageInfo.cost,
                })}
              />
            </CreditTitle>
          </CreditPackageInfo>
        )}
        <ButtonWrapper>
          <Button
            iconSrc={
              isBought ? icons.navigation.check : icons.navigation.arrowRight
            }
            text={buttonText}
            onClick={() => {}}
            focusId={`${focusIds.SUBSCRIPTION}_button_${i}`}
            expand
            iconPositionEnd
          />
        </ButtonWrapper>
      </SubscriptionCard>
    </SubscriptionNode>
  )
}

export const SubscriptionItem = memo(SubscriptionItemRaw)
